'use client'

import React, { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'

const EMAIL_SUGGESTIONS_HIDE_DELAY = 150

const EMAIL_DOMAINS = [
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'outlook.com.br',
  'yahoo.com',
  'yahoo.com.br',
] as const

export function buildEmailSuggestions(value: string): string[] {
  if (!value.includes('@') || /@.*\./.test(value)) return []
  const prefix = value.slice(0, value.indexOf('@'))
  return EMAIL_DOMAINS.map(domain => `${prefix}@${domain}`)
}

export function EmailSuggestions({
  suggestions,
  onSelect,
  focusedIndex = -1,
}: {
  suggestions: string[]
  onSelect: (value: string) => void
  focusedIndex?: number
}) {
  if (suggestions.length === 0) return null
  return (
    <div
      className="absolute right-0 left-0 z-50 rounded bg-white py-2"
      style={{ boxShadow: '2px 6px 20px 0px #828282' }}
    >
      {suggestions.map((suggestion, index) => (
        <button
          className={`w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-gray-100 ${
            index === focusedIndex ? 'bg-gray-100' : ''
          }`}
          key={suggestion}
          onClick={() => onSelect(suggestion)}
          type="button"
        >
          {suggestion}
        </button>
      ))}
    </div>
  )
}

type EmailInputWithSuggestionsProps = React.ComponentProps<typeof Input> & {
  onValueChange: (value: string) => void
}

export const EmailInputWithSuggestions = React.forwardRef<
  HTMLInputElement,
  EmailInputWithSuggestionsProps
>(({ onChange, onBlur, onKeyDown, onValueChange, ...props }, ref) => {
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  useEffect(() => () => clearTimeout(hideTimerRef.current), [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e)
    setSuggestions(buildEmailSuggestions(e.target.value))
    setFocusedIndex(-1)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    clearTimeout(hideTimerRef.current)
    hideTimerRef.current = setTimeout(() => {
      setSuggestions([])
      setFocusedIndex(-1)
    }, EMAIL_SUGGESTIONS_HIDE_DELAY)
    onBlur?.(e)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e)
    if (suggestions.length === 0) {
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex(prev => (prev >= suggestions.length - 1 ? -1 : prev + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex(prev => (prev <= -1 ? suggestions.length - 1 : prev - 1))
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault()
      onValueChange(suggestions[focusedIndex])
      setSuggestions([])
      setFocusedIndex(-1)
    } else if (e.key === 'Escape') {
      setSuggestions([])
      setFocusedIndex(-1)
    }
  }

  return (
    <div className="relative">
      <Input
        autoCapitalize="none"
        autoComplete="email"
        autoCorrect="off"
        inputMode="email"
        ref={ref}
        spellCheck={false}
        type="email"
        {...props}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      <EmailSuggestions
        focusedIndex={focusedIndex}
        onSelect={value => {
          onValueChange(value)
          setSuggestions([])
          setFocusedIndex(-1)
        }}
        suggestions={suggestions}
      />
    </div>
  )
})
EmailInputWithSuggestions.displayName = 'EmailInputWithSuggestions'
